import { ProductDesign } from '../data/mockData';
import { textures } from '../data/texture';
import { requestAIWithCache } from './requestAIWithCache';
import { exportImages } from './render3d';
import { TransformedLayer } from '../utils/resourceUtils';
import {renderLayerOnFabricCanvas} from './renderLayer';
import {
  Canvas as FabricCanvas, 
} from 'fabric';

// 全局变量存储 mergedImages 数据
interface MergedImageData {
  image: string;
  code: string;
  timestamp: number;
}


// 全局存储数组，直接存储 MergedImageData 数组
const globalMergedImages: MergedImageData[] = [];

// 获取全局 mergedImages 数据的函数
export const getGlobalMergedImages = (code: string): MergedImageData[] => {
  return globalMergedImages.filter(item => item.code === code);
};

export const loadImage = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = (e) => reject(e);
  img.src = src;
});

const imageCacheMap = new Map<string, HTMLImageElement>();
const imageInflightMap = new Map<string, Promise<HTMLImageElement>>();

// 获取所有全局 mergedImages 数据的函数
export const getAllGlobalMergedImages = (): MergedImageData[] => {
  return globalMergedImages;
};

// 清除指定 code 的全局数据
export const clearGlobalMergedImages = (code: string): void => {
  const index = globalMergedImages.findIndex(item => item.code === code);
  if (index !== -1) {
    globalMergedImages.splice(index, 1);
  }
};

// 处理模板图片的函数
export const processDesignImages = async (imageUrl: string, template: any): Promise<any[]> => {
  const mergedImages = await mergeDesignToImage(imageUrl, template.design, 800);
  // 动态导入 exportImages 函数
  const tempImageAll: any[] = await exportImages(
    { "screenshot": mergedImages, "urlPath": "http://localhost:3000/" },
    template.design
  );
  return tempImageAll;
};

// 处理跨域图片加载的辅助函数
export async function loadImageWithCORS(url: string): Promise<HTMLImageElement> {
  if (imageCacheMap.has(url)) return Promise.resolve(imageCacheMap.get(url) as HTMLImageElement);
  if (imageInflightMap.has(url)) return imageInflightMap.get(url) as Promise<HTMLImageElement>;
  const p = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    // 设置跨域属性
    img.crossOrigin = 'anonymous';

    img.onload = () => { imageCacheMap.set(url, img); imageInflightMap.delete(url); resolve(img); };
    img.onerror = async (error) => {
      console.warn('跨域图片加载失败，尝试其他方法:', url, error);

      try {
        // 尝试使用fetch API获取图片数据
        const response = await fetch(url, {
          mode: 'cors',
          headers: {
            'Accept': 'image/*'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        const objectURL = URL.createObjectURL(blob);

        const img2 = new Image();
        img2.onload = () => { URL.revokeObjectURL(objectURL); imageCacheMap.set(url, img2); imageInflightMap.delete(url); resolve(img2); };
        img2.onerror = () => {
          URL.revokeObjectURL(objectURL);
          reject(new Error(`无法加载图片: ${url}`));
        };
        img2.src = objectURL;
      } catch (fetchError) {
        console.warn('fetch方法也失败，尝试不设置跨域:', url, fetchError);

        // 最后尝试不设置跨域属性
        const img3 = new Image();
        img3.onload = () => { imageCacheMap.set(url, img3); imageInflightMap.delete(url); resolve(img3); };
        img3.onerror = () => {
          console.error('所有图片加载方法都失败:', url);
          reject(new Error(`无法加载图片: ${url}`));
        };
        img3.src = url;
      }
    };

    img.src = url;
  });
  imageInflightMap.set(url, p);
  return p;
}

// 预检查图片是否可加载（不污染Canvas）
async function canLoadImage(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

// 检查是否为base64图片
function isBase64Image(url: string): boolean {
  return url.startsWith('data:image');
}

// 检查是否为本地图片（相对路径）
function isLocalImage(url: string): boolean {
  return url.startsWith('/') || url.startsWith('./') || url.startsWith('../');
}

// 在 imageUtils.ts 中修改
const testImageCORS = async (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => resolve(true);
    img.onerror = () => {
      console.warn(`CORS 错误，图片无法加载: ${url}`);
      resolve(false);
    };

    // 设置超时
    setTimeout(() => resolve(false), 5000);

    img.src = url;
  });
};


// 使用代理服务器处理跨域图片
function getProxiedImageUrl(url: string): string {
  // 这里可以使用公共的图片代理服务
  // 例如：https://cors-anywhere.herokuapp.com/
  // 或者：https://api.allorigins.win/raw?url=

  // 注意：在生产环境中，建议使用自己的代理服务器
  // 这里仅作为示例，实际使用时需要替换为有效的代理服务
  return `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
}

// 创建一个简单的占位符图片
function createPlaceholderImage(width: number, height: number, text: string = '图片加载失败'): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return '';
  }

  canvas.width = width;
  canvas.height = height;

  // 绘制背景
  ctx.fillStyle = '#e5e7eb';
  ctx.fillRect(0, 0, width, height);

  // 绘制边框
  ctx.strokeStyle = '#d1d5db';
  ctx.lineWidth = 1;
  ctx.strokeRect(0, 0, width, height);

  // 绘制文字
  ctx.fillStyle = '#9ca3af';
  ctx.font = '14px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, width / 2, height / 2);

  return canvas.toDataURL('image/png');
}

// 清理过期的 resultImages_ 缓存
function cleanupExpiredResultImagesCache() {
  const oneHourAgo = Date.now() - (60 * 60 * 1000); // 1小时前的时间戳

  // 遍历所有localStorage中的键
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && key.startsWith('resultImages_')) {
      try {
        const cachedData = JSON.parse(localStorage.getItem(key) || '{}');
        // 检查是否有时间戳字段
        if (cachedData.timestamp && cachedData.timestamp < oneHourAgo) {
          localStorage.removeItem(key);
        }
      } catch (error) {
        // 如果解析失败，也删除这个键
        localStorage.removeItem(key);
      }
    }
  }
}


export function moveToIndex(canvas: any, object: any, index: number) {
    // 优先使用 Fabric.js API
    if (typeof canvas.moveObjectTo === 'function') {
        canvas.moveObjectTo(object, index);
        canvas.requestRenderAll();
        return;
    }
    if (typeof canvas.insertAt === 'function') {
        canvas.insertAt(object, index, true);
        canvas.requestRenderAll();
        return;
    }
    // 降级方案：直接操作对象数组
    const objs = canvas.getObjects();
    const currentIndex = objs.indexOf(object);
    if (currentIndex !== -1) {
        objs.splice(currentIndex, 1);
    }
    // 确保 index 不越界
    const targetIndex = Math.min(Math.max(0, index), objs.length);
    objs.splice(targetIndex, 0, object);
    canvas.requestRenderAll();
}

export async function mergeDesignToImage(imageUrl: string, design: ProductDesign, size: number = 400): Promise<{ image: string, code: string }[]> {

  const width = size * (design.faces?.[0]?.print_area?.width || 1);
  // 清理过期的缓存
  cleanupExpiredResultImagesCache();
  const resultImages: { image: string, code: string, timestamp: number }[] = [];
  // 遍历每个face
  if (design.faces && design.faces.length > 0) {
    for (let i = 0; i < design.faces.length; i++) {
      const face = design.faces[i];
      if (!face.layers.length && !face.background && !imageUrl) {
        continue;
      }
      // 创建canvas
      if (typeof document === 'undefined') {
        throw new Error('document not defined');
      }
      const canvasEle = document.createElement('canvas');
      if (!canvasEle) {
        throw new Error('无法创建canvas元素');
      }
      const canvas = new FabricCanvas(canvasEle, { 
        preserveObjectStacking: true,
        backgroundColor: 'rgba(0,0,0,0)' // 设置画布背景透明
      });

      // 设置canvas尺寸
      canvas.width = width;
      canvas.height = size * (face.print_area?.height || 1);

      // 渲染背景 - 使用设计级别的背景，如果没有则透明
      const height = size * (face.print_area?.height || 1);
      if (face.background && (face.background.image || face.background.texture || face.background.color)) {
        // @ts-ignore
        await renderBackground(canvas, face.background, width, height);
      }
      if (imageUrl) {
        let layer = {
          id: imageUrl,
          type: "Image" as "Image",
          src: imageUrl,
          left: 0,
          top: 0,
          width: 1,
          height: 1,
          lock: false,
        } as TransformedLayer;
        face.layers = [layer]
      }
      // 用于跟踪是否有图片加载失败
      let hasImageLoadError = false;

      // 获取print_area信息
      const printArea = face.print_area ? {
        left: face.print_area.left ?? 0,
        top: face.print_area.top ?? 0,
        width: face.print_area.width ?? 1,
        height: face.print_area.height ?? 1,
      } : {
        left: 0,
        top: 0,
        width: 1,
        height: 1
      }; 
      // 渲染每个图层
      let index = 0;
      for (const layer of face.layers) {
        try {
          console.log(layer) 
          await renderLayerOnFabricCanvas(canvas, layer, index);
          index++;
        } catch (error) {
          console.warn('渲染图层失败:', layer, error);
          hasImageLoadError = true;
        }
      }

      // 如果有图片加载失败，返回占位符图片
      if (hasImageLoadError) {
        console.warn('由于图片加载失败，返回占位符图片');
        resultImages.push({
          image: createPlaceholderImage(width, height, '图片加载失败'),
          code: face.code || `face_${i}`,
          timestamp: Date.now()
        });
      } else {
        // 返回base64图片数据
        // @ts-ignore
        const dataUrl = canvas.toDataURL({ format: 'png' });
        let itm = {
          image: dataUrl,
          code: face.code || `face_${i}`,
          timestamp: Date.now()
        }
        //本地缓存这个数据resultImages，添加时间戳
        const cacheData = {
          ...itm,
          timestamp: Date.now()
        };
        let facecode = face.code;
        // localStorage.setItem(`resultImages_${code}_${itm.code}`, JSON.stringify(cacheData));

        // 更新全局变量数组
        // 检查是否已存在相同的图片
        const existingIndex = globalMergedImages.findIndex(
          item => item.code === facecode
        );
        if (existingIndex === -1) {
          // 如果不存在，则添加新项
          globalMergedImages.push({
            image: itm.image,
            code: facecode,
            timestamp: Date.now()
          });
        } else {
          // 如果存在，则更新现有项
          globalMergedImages[existingIndex] = {
            image: itm.image,
            code: facecode,
            timestamp: Date.now()
          };
        }

        resultImages.push(itm);
      }
    }
  } else {
    // 如果没有faces，返回空数组
    console.warn('没有找到faces数据');
  }
  return resultImages;
}


// 等比例绘制图片的辅助函数（短边铺满）
function drawImageWithAspectRatio(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number
): void {
  const imgAspectRatio = img.width / img.height;
  const targetAspectRatio = w / h;

  let drawWidth = w;
  let drawHeight = h;
  let drawX = x;
  let drawY = y;

  if (imgAspectRatio > targetAspectRatio) {
    // 图片更宽，以高度为准，宽度等比例缩放
    drawHeight = h;
    drawWidth = h * imgAspectRatio;
    drawX = x + (w - drawWidth) / 2; // 水平居中
  } else {
    // 图片更高，以宽度为准，高度等比例缩放
    drawWidth = w;
    drawHeight = w / imgAspectRatio;
    drawY = y + (h - drawHeight) / 2; // 垂直居中
  }
  ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
}

// 等比例绘制 frame 图片的辅助函数（考虑 frame 样式，不超出范围）
function drawFrameImageWithAspectRatio(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
  frameStyle?: string
): void {
  const imgAspectRatio = img.width / img.height;
  const targetAspectRatio = w / h;

  let drawWidth = w;
  let drawHeight = h;
  let drawX = x;
  let drawY = y;
  if (frameStyle === 'rounded') {
    // 对于 rounded 样式，使用 120% 缩放，但保持等比例
    const scale = 1.2;
    const scaledW = w * scale;
    const scaledH = h * scale;
    if (imgAspectRatio > targetAspectRatio) {
      // 图片更宽，以高度为准
      drawHeight = scaledH;
      drawWidth = scaledH * imgAspectRatio;
      drawX = x + (w - drawWidth) / 2;
      drawY = y + (h - drawHeight) / 2;
    } else {
      // 图片更高，以宽度为准
      drawWidth = scaledW;
      drawHeight = scaledW / imgAspectRatio;
      drawX = x + (w - drawWidth) / 2;
      drawY = y + (h - drawHeight) / 2;
    }
  } else {
    // 其他样式使用标准等比例绘制
    if (imgAspectRatio > targetAspectRatio) {
      drawHeight = h;
      drawWidth = h * imgAspectRatio;
      drawX = x + (w - drawWidth) / 2;
    } else {
      drawWidth = w;
      drawHeight = w / imgAspectRatio;
      drawY = y + (h - drawHeight) / 2;
    }
  }
  // 使用 clip 确保不超出 frame 范围
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();

  ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

  ctx.restore();
}

// 渲染单个图层的辅助函数
async function renderLayer(
  ctx: CanvasRenderingContext2D,
  layer: any,
  printArea: { left: number; top: number; width: number; height: number },
  canvasWidth: number,
  canvasHeight: number
): Promise<void> {
  const { type, url, text, options, ai, shapeType } = layer;
  if (!options) return;
  // 计算绝对位置和尺寸（按照LayerRenderer的逻辑）
  const printAreaLeft = printArea.left;
  const printAreaTop = printArea.top;
  const printAreaWidth = printArea.width;
  const printAreaHeight = printArea.height;
  // 数据中给的是相对于print_area的相对位置，需要还原为绝对位置
  let absoluteLeft = options.left
  let absoluteTop = options.top
  const absoluteWidth = options.width;
  const absoluteHeight = options.height;

  // 对text的位置做特殊处理（按照LayerRenderer的逻辑）
  if (type === 'text' && options.fontFamily && options.fontSize) {
    absoluteLeft = absoluteLeft + 0.017;
    absoluteTop = absoluteTop - 0.00025 * options.fontSize;
  }
  // 转换为canvas坐标
  const x = absoluteLeft * canvasWidth;
  const y = absoluteTop * canvasHeight;
  const w = absoluteWidth * canvasWidth;
  const h = absoluteHeight * canvasHeight;

  // 保存当前状态
  ctx.save();

  // 应用旋转（按照LayerRenderer的逻辑）
  if (options.rotation && options.rotation !== 0) {
    const centerX = x + w / 2;
    const centerY = y + h / 2;
    ctx.translate(centerX, centerY);
    ctx.rotate((options.rotation * Math.PI) / 180);
    ctx.translate(-centerX, -centerY);
  }

  switch (type) {
    case 'text':
      if (text) {
        // 计算字体大小（按照LayerRenderer的缩放逻辑）
        const scale = canvasWidth * printAreaWidth / (500 * printAreaWidth);
        const fontSize = (options.fontSize || 16) * scale;

        ctx.font = `${options.fontWeight || 'normal'} ${fontSize}px ${options.fontFamily || 'Arial'}`;
        ctx.fillStyle = options.fill || '#000000';
        ctx.textAlign = (options.textAlign as CanvasTextAlign) || 'left';
        ctx.textBaseline = 'top'; // 设置文本基线为顶部，这样y坐标就是文本的顶部位置

        const textMetrics = ctx.measureText(text);

        // 计算行高（通常为字体大小的1.2倍）
        const lineHeight = fontSize * 2;

        // 绘制文本背景
        if (options.textBackgroundColor) {
          const textWidth = textMetrics.width;
          const textHeight = lineHeight; // 使用行高而不是字体大小
          ctx.fillStyle = options.textBackgroundColor;
          ctx.fillRect(x, y, textWidth, textHeight);
          ctx.fillStyle = options.fill || '#000000';
        }

        // 调整文本的y位置，使其在行高范围内垂直居中
        const textY = y + (lineHeight - fontSize) / 2;
        ctx.fillText(text, x, textY);
      }
      break;

    case 'image':
      if (url || ai) {
        try {
          let img: HTMLImageElement;
          let imageUrl = url;
          // 如果有AI属性，先尝试生成AI图片
          if (ai) {
            try {
              const dataStr = JSON.stringify({
                code: ai,
                url: url,
              });
              const response = await requestAIWithCache(dataStr);
              if (response.src) {
                imageUrl = response.src;
              }
            } catch (aiError) {
              console.warn('AI生成图片失败:', aiError);
              return; // 如果AI生成失败且没有原始URL，则跳过
            }
          }
          if (imageUrl) {
            if (isBase64Image(imageUrl)) {
              img = await loadImageWithCORS(imageUrl);
            } else if (isLocalImage(imageUrl)) {
              img = new Image();
              await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = imageUrl;
              });
            } else {
              imageUrl += "?x-oss-process=style/large";
              img = await loadImageWithCORS(imageUrl);
            }
            drawImageWithAspectRatio(ctx, img, x, y, w, h);
          }
        } catch (error) {
          console.warn('无法加载图片:', url, error);
        }
      }
      break;

    case 'frame':
      if (url || ai) {
        try {
          let img: HTMLImageElement;
          let imageUrl = url;
          // 如果有AI属性，先尝试生成AI图片
          if (ai) {
            try {
              const dataStr = JSON.stringify({
                code: ai,
                url: url,
              });
              const response = await requestAIWithCache(dataStr);
              if (response.src) {
                imageUrl = response.src;
                console.log('AI生成图片成功:', imageUrl);
              }
            } catch (aiError) {
              console.warn('AI生成图片失败:', aiError);
              return; // 如果AI生成失败且没有原始URL，则跳过
            }
          }

          if (imageUrl) {
            if (isBase64Image(imageUrl)) {
              img = await loadImageWithCORS(imageUrl);
            } else if (isLocalImage(imageUrl)) {
              img = new Image();
              await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = imageUrl;
              });
            } else {
              // const supportsCORS = await testImageCORS(imageUrl);
              // if (!supportsCORS) {
              //   console.warn('图片不支持CORS，跳过:', imageUrl);
              //   return;
              // }
              imageUrl += "?x-oss-process=style/large";
              img = await loadImageWithCORS(imageUrl);
            }

            // 绘制边框（与 LayerRenderer.tsx 保持一致）
            const frameSize = options.frameSize || 0;
            if (frameSize > 0) {
              ctx.strokeStyle = options.fill || '#000000';
              ctx.lineWidth = frameSize;

              if (options.frameStyle === 'rounded') {
                const radius = 20;
                ctx.beginPath();
                ctx.roundRect(x, y, w, h, radius);
                ctx.stroke();
              } else if (options.frameStyle === 'circle') {
                ctx.beginPath();
                ctx.arc(x + w / 2, y + h / 2, Math.min(w, h) / 2, 0, 2 * Math.PI);
                ctx.stroke();
              } else {
                ctx.strokeRect(x, y, w, h);
              }
            }
            // 绘制图片（等比例绘制，短边铺满）
            if (options.frameStyle === 'rounded') {
              const radius = 20;
              ctx.beginPath();
              ctx.roundRect(x, y, w, h, radius);
              ctx.clip();
              drawFrameImageWithAspectRatio(ctx, img, x, y, w, h, 'rounded');
            } else if (options.frameStyle === 'circle') {
              ctx.beginPath();
              ctx.arc(x + w / 2, y + h / 2, Math.min(w, h) / 2, 0, 2 * Math.PI);
              ctx.clip();
              drawFrameImageWithAspectRatio(ctx, img, x, y, w, h);
            } else {
              // 默认样式使用等比例绘制
              drawFrameImageWithAspectRatio(ctx, img, x, y, w, h);
            }
          }
        } catch (error) {
          console.warn('无法加载图片:', url, error);
        }
      }
      break;

    case 'shape':
      ctx.fillStyle = options.fill || '#000000';

      // 对于diamond形状，需要额外的45度旋转（按照LayerRenderer的逻辑）
      if (shapeType === 'diamond') {
        const centerX = x + w / 2;
        const centerY = y + h / 2;
        ctx.translate(centerX, centerY);
        ctx.rotate((45 * Math.PI) / 180); // 额外的45度旋转
        ctx.translate(-centerX, -centerY);
      }

      switch (shapeType) {
        case 'circle':
          ctx.beginPath();
          ctx.arc(x + w / 2, y + h / 2, Math.min(w, h) / 2, 0, 2 * Math.PI);
          ctx.fill();
          break;

        case 'square':
          ctx.fillRect(x, y, w, h);
          break;

        case 'rounded':
          const radius = 10;
          ctx.beginPath();
          ctx.roundRect(x, y, w, h, radius);
          ctx.fill();
          break;

        case 'triangle':
          ctx.beginPath();
          ctx.moveTo(x + w / 2, y);
          ctx.lineTo(x, y + h);
          ctx.lineTo(x + w, y + h);
          ctx.closePath();
          ctx.fill();
          break;

        case 'diamond':
          ctx.beginPath();
          ctx.moveTo(x + w / 2, y);
          ctx.lineTo(x + w, y + h / 2);
          ctx.lineTo(x + w / 2, y + h);
          ctx.lineTo(x, y + h / 2);
          ctx.closePath();
          ctx.fill();
          break;

        case 'oval':
          ctx.beginPath();
          ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, 2 * Math.PI);
          ctx.fill();
          break;

        case 'hexagon':
          ctx.beginPath();
          const hexRadius = Math.min(w, h) / 2;
          for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3;
            const hexX = x + w / 2 + hexRadius * Math.cos(angle);
            const hexY = y + h / 2 + hexRadius * Math.sin(angle);
            if (i === 0) {
              ctx.moveTo(hexX, hexY);
            } else {
              ctx.lineTo(hexX, hexY);
            }
          }
          ctx.closePath();
          ctx.fill();
          break;

        case 'star':
          // 绘制五角星
          ctx.beginPath();
          const starRadius = Math.min(w, h) / 2;
          for (let i = 0; i < 10; i++) {
            const angle = (i * Math.PI) / 5;
            const radius = i % 2 === 0 ? starRadius : starRadius * 0.4;
            const starX = x + w / 2 + radius * Math.cos(angle - Math.PI / 2);
            const starY = y + h / 2 + radius * Math.sin(angle - Math.PI / 2);
            if (i === 0) {
              ctx.moveTo(starX, starY);
            } else {
              ctx.lineTo(starX, starY);
            }
          }
          ctx.closePath();
          ctx.fill();
          break;

        case 'heart':
          // 绘制心形
          ctx.beginPath();
          const heartSize = Math.min(w, h) / 2;
          const heartX = x + w / 2;
          const heartY = y + h / 2;
          ctx.moveTo(heartX, heartY + heartSize / 4);
          ctx.bezierCurveTo(heartX, heartY, heartX - heartSize / 2, heartY, heartX - heartSize / 2, heartY + heartSize / 4);
          ctx.bezierCurveTo(heartX - heartSize / 2, heartY + heartSize / 2, heartX, heartY + heartSize / 2, heartX, heartY + heartSize);
          ctx.bezierCurveTo(heartX, heartY + heartSize / 2, heartX + heartSize / 2, heartY + heartSize / 2, heartX + heartSize / 2, heartY + heartSize / 4);
          ctx.bezierCurveTo(heartX + heartSize / 2, heartY, heartX, heartY, heartX, heartY + heartSize / 4);
          ctx.fill();
          break;

        default:
          ctx.fillRect(x, y, w, h);
      }
      break;

    case 'print_area':
      // print_area使用绝对位置，不做相对处理
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
      break;
  }

  // 恢复状态
  ctx.restore();
}

// 渲染背景的辅助函数
async function renderBackground(
  ctx: CanvasRenderingContext2D,
  background: any,
  canvasWidth: number,
  canvasHeight: number
): Promise<void> {
  if (!background) {
    // 没有背景时保持透明，不绘制任何背景
    return;
  }

  // 优先使用背景图片
  if (background.image) {
    try {
      let img: HTMLImageElement;

      if (isBase64Image(background.image)) {
        img = await loadImageWithCORS(background.image);
      } else if (isLocalImage(background.image)) {
        img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = background.image;
        });
      } else {
        img = await loadImageWithCORS(background.image + "?x-oss-process=style/large");
      }

      // 绘制背景图片，等比例绘制覆盖整个canvas
      drawImageWithAspectRatio(ctx, img, 0, 0, canvasWidth, canvasHeight);
    } catch (error) {
      console.warn('无法加载背景图片，使用默认背景:', error);
      ctx.fillStyle = background.color || '#f3f4f6';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    }
  } else if (background.color) {
    // 使用背景颜色
    ctx.fillStyle = background.color;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  } else if (background.texture) {
    // 使用纹理背景
    renderTextureBackground(ctx, background.texture, canvasWidth, canvasHeight);
  } else {
    // 默认背景色
    ctx.fillStyle = '#f3f4f6'; // bg-gray-100
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  }
}

// 渲染纹理背景
function renderTextureBackground(
  ctx: CanvasRenderingContext2D,
  texture: string,
  canvasWidth: number,
  canvasHeight: number
): void {
  // 查找对应的纹理定义
  const textureItem = textures.find(t => t.id === texture);

  if (!textureItem) {
    // 默认背景色
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    return;
  }

  // 根据texture.ts中定义的样式渲染纹理
  switch (texture) {
    case 'dots':
      // radial-gradient(circle, #ccc 1px, transparent 1px)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      ctx.fillStyle = '#cccccc';
      const dotSpacing = 10;
      const dotSize = 1;

      for (let x = dotSpacing; x < canvasWidth; x += dotSpacing) {
        for (let y = dotSpacing; y < canvasHeight; y += dotSpacing) {
          ctx.beginPath();
          ctx.arc(x, y, dotSize, 0, 2 * Math.PI);
          ctx.fill();
        }
      }
      break;

    case 'checkerboard':
      // linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)
      const squareSize = 10;
      const cols = Math.ceil(canvasWidth / squareSize);
      const rows = Math.ceil(canvasHeight / squareSize);

      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          if ((row + col) % 2 === 0) {
            ctx.fillStyle = '#ffffff';
          } else {
            ctx.fillStyle = '#cccccc';
          }
          ctx.fillRect(col * squareSize, row * squareSize, squareSize, squareSize);
        }
      }
      break;

    case 'lines-horizontal':
      // linear-gradient(90deg, #ccc 1px, transparent 1px)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 1;
      const hLineSpacing = 10;

      for (let y = 0; y < canvasHeight; y += hLineSpacing) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvasWidth, y);
        ctx.stroke();
      }
      break;

    case 'lines-vertical':
      // linear-gradient(0deg, #ccc 1px, transparent 1px)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 1;
      const vLineSpacing = 10;

      for (let x = 0; x < canvasWidth; x += vLineSpacing) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvasHeight);
        ctx.stroke();
      }
      break;

    case 'lines':
      // repeating-linear-gradient(45deg, transparent, transparent 2px, #ccc 2px, #ccc 4px)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 2;
      const dLineSpacing = 10;

      // 绘制45度对角线
      for (let i = -canvasHeight; i < canvasWidth + canvasHeight; i += dLineSpacing) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i + canvasHeight, canvasHeight);
        ctx.stroke();
      }
      break;

    case 'grid':
      // linear-gradient(#ccc 1px, transparent 1px), linear-gradient(90deg, #ccc 1px, transparent 1px)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 1;
      const gridSize = 10;

      // 垂直线
      for (let x = 0; x <= canvasWidth; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvasHeight);
        ctx.stroke();
      }

      // 水平线
      for (let y = 0; y <= canvasHeight; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvasWidth, y);
        ctx.stroke();
      }
      break;

    case 'waves':
      // repeating-linear-gradient(45deg, transparent, transparent 10px, #ccc 10px, #ccc 12px)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 2;
      const waveSpacing = 10;

      // 绘制波浪线
      for (let y = 0; y < canvasHeight; y += waveSpacing) {
        ctx.beginPath();
        for (let x = 0; x < canvasWidth; x += 2) {
          const waveY = y + Math.sin(x * 0.1) * 3;
          if (x === 0) {
            ctx.moveTo(x, waveY);
          } else {
            ctx.lineTo(x, waveY);
          }
        }
        ctx.stroke();
      }
      break;

    default:
      // 默认背景色
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  }
} 
